import { useEffect, useState } from "react";
import { Circle } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { useAuth } from "@/context/AuthContext";

const TK_CLASSES = ["", "TK B", "TK A", "KB"];
const SD_CLASSES = ["", "Kelas 1", "Kelas 2", "Kelas 3", "Kelas 4", "Kelas 5", "Kelas 6"];
const fmt = (iso) => { if (!iso) return "-"; try { return new Date(iso).toLocaleString("id-ID", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

export default function MonitorUsers() {
  const { user } = useAuth();
  const CLASSES = user?.school === "sdqu" ? SD_CLASSES : TK_CLASSES;
  const [date, setDate] = useState("");
  const [kelas, setKelas] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    const fetchData = () => {
      const qs = new URLSearchParams();
      if (date) qs.set("date", date);
      if (kelas) qs.set("kelas", kelas);
      api.get(`/admin/login-monitor?${qs.toString()}`).then((r) => setData(r.data));
    };
    fetchData();
    const t = setInterval(fetchData, 15000);
    return () => clearInterval(t);
  }, [date, kelas]);

  const Group = ({ title, items, dot, testid }) => (
    <div className="mb-4" data-testid={testid}>
      <p className="font-heading font-semibold text-stone-800 mb-2 flex items-center gap-2"><Circle size={12} weight="fill" className={dot} /> {title} ({items?.length || 0})</p>
      <div className="space-y-1.5">
        {(items || []).map((u) => (
          <div key={u.id} className="bg-white rounded-xl border border-stone-200/60 p-3 flex items-center justify-between shadow-sm">
            <div><p className="font-semibold text-stone-800 text-sm">{u.name}</p>
              <p className="text-stone-400 text-xs">{u.role === "guru" ? "Guru" : "Orang Tua"}{u.kelas ? ` • ${u.kelas}` : ""}</p></div>
            <span className="text-[11px] text-stone-400">{fmt(u.last_login)}</span>
          </div>
        ))}
        {(!items || items.length === 0) && <p className="text-stone-400 text-xs">Tidak ada.</p>}
      </div>
    </div>
  );

  const GroupedByKelas = ({ title, items, dot, testid }) => {
    const groups = {};
    (items || []).forEach((u) => { const k = u.kelas || "Tanpa Kelas"; (groups[k] = groups[k] || []).push(u); });
    const keys = Object.keys(groups).sort();
    return (
      <div className="mb-4" data-testid={testid}>
        <p className="font-heading font-semibold text-stone-800 mb-2 flex items-center gap-2"><Circle size={12} weight="fill" className={dot} /> {title} ({items?.length || 0})</p>
        {keys.length === 0 && <p className="text-stone-400 text-xs">Tidak ada.</p>}
        {keys.map((k) => (
          <div key={k} className="mb-2.5">
            <p className="text-xs font-bold text-primary bg-primary/10 inline-block px-2.5 py-0.5 rounded-full mb-1.5">{k}</p>
            <div className="space-y-1.5">
              {groups[k].map((u) => (
                <div key={u.id} className="bg-white rounded-xl border border-stone-200/60 p-3 flex items-center justify-between shadow-sm">
                  <p className="font-semibold text-stone-800 text-sm">{u.name}</p>
                  <span className="text-[11px] text-stone-400">{u.last_login ? fmt(u.last_login) : "Belum pernah"}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div>
      <PageHeader title="Monitor Pengguna" />
      <div className="px-5 pt-4 pb-8">
        <div data-testid="online-counter" className="bg-gradient-to-r from-green-600 to-emerald-500 text-white rounded-2xl p-4 mb-4 flex items-center gap-3 shadow-sm">
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white/70" />
            <span className="relative inline-flex rounded-full h-3 w-3 bg-white" />
          </span>
          <div>
            <p className="font-heading font-bold text-2xl leading-none" data-testid="online-count">{data?.online?.length ?? 0}</p>
            <p className="text-white/85 text-xs mt-0.5">pengguna online saat ini</p>
          </div>
        </div>
        <div className="flex gap-2 mb-4">
          <input data-testid="monitor-date" type="date" value={date} onChange={(e) => setDate(e.target.value)}
            className="flex-1 bg-white border border-stone-200 rounded-xl py-2.5 px-3 text-sm outline-none focus:border-primary" />
          <select data-testid="monitor-kelas" value={kelas} onChange={(e) => setKelas(e.target.value)}
            className="shrink-0 max-w-[9rem] bg-white border border-stone-200 rounded-xl py-2.5 px-3 text-sm outline-none focus:border-primary">
            {CLASSES.map((c) => <option key={c} value={c}>{c || "Semua Kelas"}</option>)}
          </select>
        </div>
        {!data ? <p className="text-center text-stone-400 mt-8">Memuat...</p> : (
          <>
            <Group title="Sedang Online" items={data.online} dot="text-green-500" testid="monitor-online" />
            <Group title={`Sudah Login (${data.date})`} items={data.today} dot="text-sky-500" testid="monitor-today" />
            <GroupedByKelas title="Guru Belum Login Hari Ini" items={(data.not_today || []).filter((u) => u.role === "guru")} dot="text-stone-300" testid="monitor-guru-not-today" />
            <GroupedByKelas title="Orang Tua Belum Login Hari Ini" items={(data.not_today || []).filter((u) => u.role === "orangtua")} dot="text-stone-300" testid="monitor-ortu-not-today" />
            <p className="font-heading font-semibold text-stone-800 mb-2 mt-6">Riwayat Login {date ? `(${date})` : "(30 hari terakhir)"}</p>
            <div className="space-y-1.5" data-testid="monitor-history">
              {data.history.map((e, i) => (
                <div key={i} className="bg-white rounded-xl border border-stone-200/60 p-2.5 flex items-center justify-between shadow-sm">
                  <span className="text-sm text-stone-700">{e.name} <span className="text-stone-400 text-xs">• {e.role === "guru" ? "Guru" : e.role === "admin" ? "Admin" : "Ortu"}{e.kelas ? ` • ${e.kelas}` : ""}</span></span>
                  <span className="text-[11px] text-stone-400">{fmt(e.at)}</span>
                </div>
              ))}
              {data.history.length === 0 && <p className="text-stone-400 text-xs">Belum ada riwayat.</p>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
